import React, { useState, useEffect } from "react";
import { api } from "../../api";
import { Spinner } from "../../components/ui";
import {
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  CartesianGrid, AreaChart, Area,
} from "recharts";

const STATUS_COLOR = {
  Approved: "#16A34A", Denied: "#DC2626", Canceled: "#D97706", Withdraw: "#6B7280",
  Pending: "#CA8A04", Reviewed: "#2563EB", "Not Required": "#94A3B8",
};
const WORK_COLOR = { Unassigned: "#94A3B8", Assigned: "#2F80C4", "In progress": "#CA8A04", Done: "#16A34A" };
const STAGE_LABEL = { SUBMISSION: "Submission", END_PROCESS: "End Process", MODIFICATION: "Modification" };
const BRAND = "#2F80C4";

const toData = (obj, colors) => Object.entries(obj || {}).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value, fill: colors?.[name] || BRAND }));

function Kpi({ label, value, tone }) {
  return <div className={"kpi" + (tone ? " kpi-" + tone : "")}><div className="kpi-value">{value ?? "—"}</div><div className="kpi-label">{label}</div></div>;
}
function ChartCard({ title, children, tall }) {
  return <div className="panel"><div className="panel-title">{title}</div><div style={{ width: "100%", height: tall ? 300 : 240 }}><ResponsiveContainer>{children}</ResponsiveContainer></div></div>;
}
function ago(iso) {
  if (!iso) return "never";
  const d = (Date.now() - new Date(iso).getTime()) / 86400000;
  if (d < 1) return "today"; if (d < 2) return "yesterday"; return Math.floor(d) + "d ago";
}

export function Dashboard({ user }) {
  const [ov, setOv] = useState(null);
  const [prod, setProd] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    Promise.all([api.statsOverview(), api.statsProduction()]).then(([o, p]) => { setOv(o); setProd(p); }).catch((e) => setErr(e.message));
  }, []);
  if (err) return <div className="err-text" style={{ padding: 18 }}>{err}</div>;
  if (!ov || !prod) return <div className="empty"><Spinner /></div>;

  const statusData = toData(ov.byStatus, STATUS_COLOR);
  const workData = toData(ov.workStatus, WORK_COLOR);
  const stageData = Object.entries(ov.byStage || {}).map(([k, v]) => ({ name: STAGE_LABEL[k] || k, value: v }));
  const inactive = prod.filter((p) => p.status === "ACTIVE" && !p.active);
  const topProd = [...prod].sort((a, b) => b.done - a.done).slice(0, 8)
    .map((p) => ({ name: p.name.split(" ")[0], Done: p.done, Today: p.workedToday }));

  return (
    <div>
      <div className="kpi-row">
        <Kpi label="Imported today" value={ov.importedToday} tone="blue" />
        <Kpi label="Unassigned" value={ov.unassigned} tone="amber" />
        <Kpi label="In progress" value={ov.inProgress} />
        <Kpi label="Worked today" value={ov.workedToday} tone="green" />
        <Kpi label="Completed" value={ov.done} tone="green" />
        <Kpi label="Total records" value={ov.total} />
      </div>

      <ChartCard title="Activity — last 14 days" tall>
        <AreaChart data={ov.trend} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
          <defs>
            <linearGradient id="gImp" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={BRAND} stopOpacity={0.35} /><stop offset="100%" stopColor={BRAND} stopOpacity={0} /></linearGradient>
            <linearGradient id="gWrk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#16A34A" stopOpacity={0.35} /><stop offset="100%" stopColor="#16A34A" stopOpacity={0} /></linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip /><Legend />
          <Area type="monotone" dataKey="imported" name="Imported" stroke={BRAND} fill="url(#gImp)" strokeWidth={2} />
          <Area type="monotone" dataKey="worked" name="Worked" stroke="#16A34A" fill="url(#gWrk)" strokeWidth={2} />
        </AreaChart>
      </ChartCard>

      <div className="dash-2col">
        <ChartCard title="Outcomes by status">
          <PieChart>
            <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={2} label={(d) => d.name}>
              {statusData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ChartCard>
        <ChartCard title="Work status">
          <PieChart>
            <Pie data={workData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} label={(d) => `${d.name}: ${d.value}`}>
              {workData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ChartCard>
      </div>

      <div className="dash-2col">
        <ChartCard title="Records by stage">
          <BarChart data={stageData} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip /><Bar dataKey="value" name="Records" fill={BRAND} radius={[6, 6, 0, 0]} barSize={48} />
          </BarChart>
        </ChartCard>
        <ChartCard title="Top production (completed)">
          <BarChart data={topProd} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef1f5" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} /><YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
            <Tooltip /><Legend />
            <Bar dataKey="Done" fill={BRAND} radius={[6, 6, 0, 0]} />
            <Bar dataKey="Today" fill="#16A34A" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ChartCard>
      </div>

      <div className="panel">
        <div className="panel-title">Production by user {inactive.length > 0 && <span className="pill role-admin" style={{ marginLeft: 8 }}><span className="dot" />{inactive.length} inactive today</span>}</div>
        <div className="table-wrap">
          <table className="tbl">
            <thead><tr><th></th><th>User</th><th>Processes</th><th>Open</th><th>Today</th><th>Week</th><th>Total done</th><th>Last worked</th></tr></thead>
            <tbody>
              {prod.map((p) => (
                <tr key={p.userId}>
                  <td><span className={"activity-dot " + (p.active ? "on" : "off")} title={p.active ? "Worked today" : "No work today"} /></td>
                  <td><b>{p.name}</b></td>
                  <td className="cell-sub">{(p.processes || []).join(", ") || "—"}</td>
                  <td>{p.open}</td><td><b>{p.workedToday}</b></td><td>{p.workedWeek}</td><td>{p.done}</td>
                  <td className="cell-sub">{ago(p.lastWorked)}</td>
                </tr>
              ))}
              {prod.length === 0 && <tr><td colSpan={8} className="cell-sub" style={{ padding: 16 }}>No worker accounts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}